import { MenuItem, Divider, Checkbox } from "@mui/material";
import {
  CATEGORY_TO_LABEL_MAP,
  PNL_COLORS,
  PNL_ORDER,
} from "../../../constants/constants";
import styled from "styled-components/macro";
import { PNL_COLOR_SQUARE_BORDER } from "../../Nav/ColorLegend";
import type {
  TagDetails,
  TrendsPageFilterStateType,
} from "../../../types/types";
import { PnlCategoriesEnum } from "../../../types/enums";
import { useDispatch } from "react-redux";
import { updateTrendsPageFilterState } from "../../../providers/GlobalStateProvider";
import { isEqual } from "lodash";
import { ALL_TAG_NAME_DISPLAY } from "./filtersDrawerUtils";
import { parseTrendsPageFilterState } from "../trendsPageUtils";
import { useTrendsPageFilterState } from "utils/customHooks/genericHooks";
import { useTagList } from "./useTagList";
import { useProcessTilesFilterState } from "pages/DashboardPages/ProcessTilesControls/useProcessTilesFilterState";
import { useCallback, useMemo } from "react";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";

type FilterByTagDropdownProps = {
  handleClose: () => void;
  /** passing onTagSelect switches this picker list to tag-only mode by disabling clicks on the category items, only allowing clicking tag items instead */
  onTagSelect?: (tag: TagDetails) => void;
};

const FilterByTagDropdown = ({
  handleClose,
  onTagSelect,
}: FilterByTagDropdownProps): JSX.Element | null => {
  const dispatch = useDispatch();
  const { trendsPageFilterState } = useTrendsPageFilterState();
  const tagList: TagDetails[] = useTagList();

  const filteredTagList = useMemo(() => {
    // Filter out 'Producing' and 'Unknown' and 'Uncollected' tags
    const filteredTagList = onTagSelect
      ? tagList.filter(
          tag => !["Producing", "Unknown", "Uncollected"].includes(tag.name)
        )
      : tagList;

    return filteredTagList.sort((tagA, tagB) =>
      tagA.name > tagB.name ? 1 : -1
    );
  }, [tagList, onTagSelect]);

  const isReady = filteredTagList.length > 0;
  const isLoading = tagList.length === 0;

  const [filterState] = useProcessTilesFilterState();

  const handleTagOrPnlCategoryDropdownChange = useCallback(
    (newTagOrPnlCategoryFilter: string, pnlCategory: string) => {
      const isPnlCategoryFilter = newTagOrPnlCategoryFilter === pnlCategory;
      const isTagFilter = !isPnlCategoryFilter && newTagOrPnlCategoryFilter;

      const newFilterState: TrendsPageFilterStateType = {
        ...trendsPageFilterState,
        // apply the tag if necessary
        ...(isTagFilter ? { tag: newTagOrPnlCategoryFilter } : {}),
        // remove the tag if filtering by category
        ...(isPnlCategoryFilter ? { tag: ALL_TAG_NAME_DISPLAY } : {}),
        ...(pnlCategory ? { pnlCategory } : {}),
      };
      if (!isEqual(trendsPageFilterState, newFilterState)) {
        dispatch(updateTrendsPageFilterState({ newFilterState }));
      }
    },
    [dispatch, trendsPageFilterState]
  );

  const allVisiblePnlCategories = useMemo(
    () =>
      filteredTagList.reduce(
        (acc, { name, pnlCategory }) =>
          !acc.includes(pnlCategory) ? [...acc, pnlCategory] : acc,
        [] as PnlCategoriesEnum[]
      ),
    [filteredTagList]
  );

  const { translate } = useFlightTranslation();

  if (!isReady) {
    return (
      <MenuItem style={{ pointerEvents: "none" }}>
        {translate(isLoading ? "Loading..." : "-- No Tags --")}
      </MenuItem>
    );
  }

  const { isTrendsPageFilteredByTag } = parseTrendsPageFilterState({
    trendsPageFilterState,
  });
  // if fitered by tag, show the tag,
  const dropdownValue = isTrendsPageFilteredByTag
    ? trendsPageFilterState.tag
    : // otherwise check for a pnl category, and fall back to the ALL display name if not
      trendsPageFilterState.pnlCategory || trendsPageFilterState.tag;

  return (
    <>
      {onTagSelect ? null : (
        <MenuItem
          value={"All"}
          onClick={() => {
            if (dropdownValue === "All") {
              return;
            }
            handleTagOrPnlCategoryDropdownChange("All", "All");
            handleClose();
          }}
        >
          All
        </MenuItem>
      )}
      {allVisiblePnlCategories
        .sort((a, b) => (PNL_ORDER[a] < PNL_ORDER[b] ? -1 : 1))
        .map(sortedPnlCategory => {
          const tagsInThisCategory = filteredTagList.filter(
            ({ name, pnlCategory }) => pnlCategory === sortedPnlCategory
          );
          const pnlCategoryTitle = CATEGORY_TO_LABEL_MAP[sortedPnlCategory];
          return [
            <br key={0} />,
            <MenuItem
              key={sortedPnlCategory}
              style={{
                paddingTop: "0.5em !important",
                marginTop: "0.5em !important",
                pointerEvents: onTagSelect ? "none" : "auto",
              }}
              value={String(sortedPnlCategory)}
              data-value={String(sortedPnlCategory)}
              data-pnl-category={sortedPnlCategory} // used to flip the trend chart upside down if it's a loss category
              onClick={
                // since this is a pnl category menu item,
                // if we're in tag picker mode, don't allow clicking on the category, only the tags
                onTagSelect
                  ? () => {
                      return;
                    }
                  : () => {
                      if (dropdownValue === String(sortedPnlCategory)) {
                        return;
                      }
                      handleTagOrPnlCategoryDropdownChange(
                        String(sortedPnlCategory),
                        sortedPnlCategory
                      );
                      handleClose();
                    }
              }
            >
              <PnlCategoryTitleStyles>
                <div
                  className="color-square"
                  style={{ background: PNL_COLORS[sortedPnlCategory] }}
                />
                <div className="pnl-title">{pnlCategoryTitle}</div>
              </PnlCategoryTitleStyles>
            </MenuItem>,
            <Divider
              key={`${sortedPnlCategory}_divider`}
              style={{ marginTop: 0, marginBottom: 0 }}
            />,
            ...tagsInThisCategory.map(tag => (
              <MenuItem
                key={tag.name}
                value={tag.name}
                data-value={tag.name}
                data-pnl-category={tag.pnlCategory} // used to flip the trend chart upside down if it's a loss category
                onClick={() => {
                  if (dropdownValue === tag.name) {
                    return;
                  }
                  if (onTagSelect) {
                    // tag picker mode (e.g. process-tiles page)
                    onTagSelect(tag);
                  } else {
                    // trends page mode
                    handleTagOrPnlCategoryDropdownChange(
                      tag.name,
                      tag.pnlCategory
                    );
                    handleClose();
                  }
                }}
                style={{ display: "flex", alignItems: "center" }}
              >
                <div className="tag-name" style={{ flexGrow: 1 }}>
                  {tag.name}
                </div>
                {onTagSelect ? (
                  <div
                    className="check-box"
                    key={
                      filterState.filterByTagNames?.includes(tag.name)
                        ? "checked"
                        : "unchecked"
                    }
                  >
                    <Checkbox
                      checked={filterState.filterByTagNames?.includes(tag.name)}
                      size="small"
                      style={{ padding: "0 0 0 16px", marginRight: -9 }}
                    />
                  </div>
                ) : null}
              </MenuItem>
            )),
          ];
        })}
    </>
  );
};

const PnlCategoryTitleStyles = styled.div`
  font-weight: bold !important;
  display: flex;
  align-items: baseline;
  .color-square {
    box-sizing: border-box;
    max-width: 12px;
    max-height: 12px;
    min-width: 12px;
    min-height: 12px;
    border: ${PNL_COLOR_SQUARE_BORDER};
    margin-right: 0.5em;
  }
`;

export default FilterByTagDropdown;
